/**
 * Per-widget configuration: the frontend half of the dashboard widget config
 * contract (backend: shared/services/dashboard_widget_registry.py). A widget
 * declares typed `config_fields` in its backend descriptor; the shared
 * `WidgetConfigDialog` renders a settings-cog form from them and the chosen
 * values persist per-placement. A widget reads its resolved values through the
 * typed getters below. See docs/platform/dashboard-widget-registry.md.
 */

import type { WidgetConfigField } from '../../../generated/api/models/widgetConfigField';

/** A widget's resolved configuration values, keyed by config-field key. */
export type WidgetConfig = Record<string, unknown>;

/** Props every dashboard widget component accepts. `config` is the resolved
 *  per-placement settings (backend-merged with declared defaults), undefined
 *  for non-configurable widgets. */
export interface DashboardWidgetProps {
  config?: WidgetConfig;
}

/** Seed a config object from a widget's declared field defaults — used when a
 *  widget is freshly added from the picker (before the first save round-trips
 *  the backend-coerced values). Mirrors the backend `_field_default`. */
export function configDefaults(fields?: WidgetConfigField[] | null): WidgetConfig {
  const out: WidgetConfig = {};
  for (const field of fields ?? []) {
    if (field.default !== undefined && field.default !== null) {
      out[field.key] = field.default;
    } else if (field.control_type === 'multiselect') {
      out[field.key] = [];
    } else if (field.control_type === 'toggle') {
      out[field.key] = false;
    } else if (field.control_type === 'number') {
      out[field.key] = field.min ?? 0;
    }
  }
  return out;
}

export function getNumberConfig(config: WidgetConfig | undefined, key: string, fallback: number): number {
  const value = config?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function getStringConfig(config: WidgetConfig | undefined, key: string, fallback: string): string {
  const value = config?.[key];
  return typeof value === 'string' ? value : fallback;
}

export function getStringArrayConfig(
  config: WidgetConfig | undefined,
  key: string,
  fallback: string[]
): string[] {
  const value = config?.[key];
  if (!Array.isArray(value)) return fallback;
  return value.filter((item): item is string => typeof item === 'string');
}

export function getBooleanConfig(config: WidgetConfig | undefined, key: string, fallback: boolean): boolean {
  const value = config?.[key];
  return typeof value === 'boolean' ? value : fallback;
}
